import appConfig from '../../config/app.json';

/**
 * Universal Links (iOS) / App Links (Android): links to these web hosts open
 * the native app instead of the browser — e.g. the "Open your chat" button in
 * the trip-confirmed email. Which paths the OS hands to the app is decided by
 * the files the website serves (public/.well-known/apple-app-site-association
 * and assetlinks.json) and the app's build config (build-ios.sh and the
 * Android workflows); this side only routes a link once it arrives.
 *
 * The hosts live in config/app.json, which the iOS and Android builds read too.
 * The primary host is the app's address now; the older host stays listed because
 * apps installed before the move, and emails already sent, still use it.
 */
export const APP_LINK_HOSTS: string[] = appConfig.appLinkHosts;

/** The in-app route for an app link, or null if the URL isn't one. */
export function appLinkPath(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || !APP_LINK_HOSTS.includes(parsed.hostname)) return null;
  // Sign-in links never arrive this way: a crafted link to /auth/... could
  // otherwise sign the app into someone else's account.
  if (parsed.pathname === '/auth' || parsed.pathname.startsWith('/auth/')) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

/**
 * The OAuth / password-reset code from the app's own callback URL
 * (`<scheme>://auth/callback?code=…`): '' when the callback carries no code
 * (sign-in cancelled or failed), null when the URL isn't the callback. Only the
 * exact scheme, host and path count, and only a PKCE `code` is accepted: it is
 * useless without the verifier this device created when it started the
 * sign-in, so a link someone else crafted can't sign the app into their
 * account.
 */
export function authCallbackCode(url: string, scheme: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== `${scheme}:` || parsed.hostname !== 'auth' || parsed.pathname !== '/callback') return null;
  return parsed.searchParams.get('code') ?? '';
}

/**
 * Navigate the running app to `path` from outside React. BrowserRouter
 * re-reads window.location on popstate, so push the entry and announce it.
 */
export function navigateInApp(path: string) {
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (current === path) return;
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
